import { useOutletContext } from 'react-router-dom'
import { useMemo, useCallback, useState } from 'react'
import { Grid, Heading } from '@design-systems/canvas-ui'
import { Table } from 'components/table-compat/table-compat'
import { buildDataTableObject, TableData } from 'utils/data'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import {
    InfDetailColumnId,
    isInfDetailColumnId,
    sortInfDetails,
} from './sort-inf-details'
import {
    buildSummaryTable,
    buildDetailsTable,
    formattedTimeStamp,
} from '../../helpers'

type FulfillmentType = 'opu' | 'sfs'
type ActiveSort = { columnId: InfDetailColumnId; direction: 'asc' | 'desc' }
type DetailSort = ActiveSort | null

const DEFAULT_DETAIL_SORT: ActiveSort = {
    columnId: 'totalMinutes',
    direction: 'desc',
}

export const StoreFulfillmentINFStore = () => {
    const { t } = useEssentials()
    const storeFulfillment = TRANSLATION_KEYS.storeFulfillment
    const store = storeFulfillment.inf.store
    const { data } = useOutletContext<{ data: any }>()

    const [detailSorts, setDetailSorts] = useState<
        Record<FulfillmentType, DetailSort>
    >({
        opu: DEFAULT_DETAIL_SORT,
        sfs: DEFAULT_DETAIL_SORT,
    })

    const infData = data[0].inventory_not_found

    const formattedDate = `${t(storeFulfillment.timePeriods.today)} ${formattedTimeStamp(infData.order_pickup.timestamp)}`

    const fulfillmentData = useMemo(
        () => ({
            opu: {
                summaryData: infData?.order_pickup?.order_pickup_details,
                detailsData:
                    infData?.order_pickup_inventory_not_found
                        ?.order_pickup_inventory_not_found_details,
                storeKey: store.inf,
                titleKey: store.orderPickupOPU,
            },
            sfs: {
                summaryData: infData?.ship_from_store?.ship_from_store_details,
                detailsData:
                    infData?.ship_from_store_inventory_not_found
                        ?.ship_from_store_inventory_not_found_details,
                storeKey: store.sfs,
                titleKey: store.shipFromStoreSFS,
            },
        }),
        [infData, store],
    )

    const handleDetailsSort = useCallback(
        (type: FulfillmentType, columnId: InfDetailColumnId) =>
            (direction: 'asc' | 'desc' | undefined) => {
                setDetailSorts((previous) => ({
                    ...previous,
                    [type]: direction ? { columnId, direction } : null,
                }))
            },
        [],
    )

    const summaryTables = useMemo(() => {
        return {
            opu: buildDataTableObject({
                currentData: buildSummaryTable(
                    fulfillmentData.opu.summaryData,
                    fulfillmentData.opu.storeKey,
                    store,
                    t,
                ),
                widths: [125, 50, 50, 60],
            }),
            sfs: buildDataTableObject({
                currentData: buildSummaryTable(
                    fulfillmentData.sfs.summaryData,
                    fulfillmentData.sfs.storeKey,
                    store,
                    t,
                ),
                widths: [125, 50, 50, 60],
            }),
        }
    }, [fulfillmentData, store, t])

    const buildDetailsTableConfig = useCallback(
        (type: FulfillmentType) => {
            const detailsData = fulfillmentData[type].detailsData
            const originalData = buildDetailsTable(detailsData, store, t)
            const sort = detailSorts[type]
            const currentData: TableData = sort
                ? {
                      ...originalData,
                      rows: sortInfDetails(
                          originalData.rows,
                          originalData.headings ?? [],
                          sort.columnId,
                          sort.direction,
                      ),
                  }
                : originalData

            return buildDataTableObject({
                currentData,
                widths: [125, 75, 75, 100, 100, 350, 100, 90],
                columnSortSettingsByIndex: Object.fromEntries(
                    (originalData.headings ?? []).flatMap((heading, index) => {
                        const columnId = heading.id
                        if (!columnId || !isInfDetailColumnId(columnId)) {
                            return []
                        }

                        const onSort = (
                            direction: 'asc' | 'dsc' | undefined,
                        ) => {
                            // The legacy table type still accepts "dsc"; Canvas 8 emits "desc".
                            handleDetailsSort(
                                type,
                                columnId,
                            )(
                                direction === 'asc'
                                    ? 'asc'
                                    : direction
                                      ? 'desc'
                                      : undefined,
                            )
                        }

                        return [
                            [
                                index,
                                {
                                    sortable: true,
                                    onSort,
                                    defaultSort:
                                        columnId ===
                                        DEFAULT_DETAIL_SORT.columnId
                                            ? DEFAULT_DETAIL_SORT.direction
                                            : undefined,
                                },
                            ],
                        ]
                    }),
                ),
                pinFirstColumn: true,
            })
        },
        [fulfillmentData, store, t, detailSorts, handleDetailsSort],
    )

    const detailsTables = useMemo(() => {
        return {
            opu: buildDetailsTableConfig('opu'),
            sfs: buildDetailsTableConfig('sfs'),
        }
    }, [buildDetailsTableConfig])

    return (
        <Grid.Container className="hc8-mt-2x">
            <Grid.Item
                className="hc8-pa-none"
                size={{
                    xs: 12,
                }}
            >
                <Heading size={6}>{formattedDate}</Heading>
                <Heading size={6} className="hc8-pt-4x hc8-pv-3x">
                    {t(fulfillmentData.opu.titleKey)}
                </Heading>
                <Table
                    data={summaryTables.opu}
                    scrollableWidth
                    alternateRowColor
                />
                <Heading size={6} className="hc8-pt-4x hc8-pv-3x">
                    {t(fulfillmentData.sfs.titleKey)}
                </Heading>
                <Table
                    data={summaryTables.sfs}
                    scrollableWidth
                    alternateRowColor
                />
                {fulfillmentData.opu.detailsData && (
                    <>
                        <Heading size={6} className="hc8-pt-4x hc8-pv-3x">
                            {t(fulfillmentData.opu.titleKey)}
                        </Heading>

                        <Table
                            data={detailsTables.opu}
                            scrollableWidth
                            alternateRowColor
                        />
                    </>
                )}
                {fulfillmentData.sfs.detailsData && (
                    <>
                        <Heading size={6} className="hc8-pt-4x hc8-pv-3x">
                            {t(fulfillmentData.sfs.titleKey)}
                        </Heading>
                        <Table
                            data={detailsTables.sfs}
                            scrollableWidth
                            alternateRowColor
                        />
                    </>
                )}
            </Grid.Item>
        </Grid.Container>
    )
}
